import useDiscoveryFetch from './useDiscoveryFetch';
import { useState, useEffect } from 'react';
import api from '../../api/axiosInstance';
import { useParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Image,
    Spinner,
    Alert,
    Table,
    Form,
} from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import CityDetailHero from './cityDetail/CityDetailHero';
import CityDetailMobileTabs from './cityDetail/CityDetailMobileTabs';
import './cityDetail/CityUniversitiesMobile.css';
import normalizeUrl from './cityDetail/normalizeUniversityUrl';
import MobileUniversityList from './MobileUniversityList';
import Pagination from 'react-bootstrap/Pagination';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import NavTabs from '../../NavTabs';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import icNoInsights from '../../assets/img/ic-empty-box.svg';


const applySort = (arr, { by, dir }) => {
    if (!by) return arr;
    const mul = dir === 'desc' ? -1 : 1;
    return [...arr].sort((a, b) => {
        const pick = (key, obj) => {
            switch (key) {
                case 'name':           return (obj.name || '').toLowerCase().trim();
                case 'address':        return (obj.address || '').toLowerCase().trim();
                case 'telephone':      return (obj.telephone || '').toLowerCase().trim();
                case 'url':            return (obj.url || '').toLowerCase().trim();
                case 'clasification':  return (obj.classification || '').toLowerCase().trim();
                case 'studentsNumber': return Number(obj.studentsNumber || 0);
                default:               return '';
            }
        };
        const va = pick(by, a);
        const vb = pick(by, b);
        if (va < vb) return -1 * mul;
        if (va > vb) return  1 * mul;
        return 0;
    });
};

const DiscoveryCityViewUniversities = () => {
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });
    const { id, cityId } = useParams();
    const actualCityId = cityId || id;
    const countryCode = cityId ? id : null;

    const [cityData, setCityData] = useState(null);
    const [universities, setUniversities] = useState([]);
    const [filteredUniversities, setFilteredUniversities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [sort, setSort] = useState({ by: null, dir: 'asc' });
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchCity = async () => {
            try {
                if (!isReconnect || !cityData) setLoading(true);
                setError('');
                const response = await api.get(`/api/discovery/cities/${actualCityId}/universities`, { signal });
                if (!isCurrent()) return;
                const data = response.data || {};
                setCityData(data);
                const list = Array.isArray(data.universities) ? data.universities : [];
                setUniversities(list);
                setFilteredUniversities(list);
                if (!isReconnect) setCurrentPage(1);
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching city universities:', err);
                setError('An error occurred while fetching city universities.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchCity();
    }, [actualCityId]);

    // Search filter
    useEffect(() => {
        const q = searchQuery.trim().toLowerCase();
        const next = universities.filter((u) => {
            if (!q) return true;
            return [u.name, u.address, u.url]
                .map(v => String(v || '').toLowerCase())
                .some(s => s.includes(q));
        });
        setFilteredUniversities(applySort(next, sort));
    }, [searchQuery, universities]);

    useEffect(() => { setCurrentPage(1); }, [searchQuery]);

    const toggleSort = (by) => {
        setSort(prev => {
            const dir = prev.by === by ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'asc';
            const next = { by, dir };
            setFilteredUniversities(applySort(filteredUniversities, next));
            return next;
        });
    };

    const safeUrlHref = normalizeUrl(cityData?.url);

    const totalPages = Math.ceil(filteredUniversities.length / rowsPerPage);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentUniversities = filteredUniversities.slice(indexOfFirst, indexOfLast);

    const cityBasePath = countryCode
        ? `/discovery/countries/${countryCode}/cities/${actualCityId}`
        : `/discovery/cities/${actualCityId}`;
    const discoveryCityNavItems = [
        { to: cityBasePath, eventKey: 'about', label: 'General Info', end: true },
        { to: `${cityBasePath}/jobs`, eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`${cityBasePath}/companies`] },
        { to: `${cityBasePath}/salary`, eventKey: 'salary', label: 'Average salaries' },
        { to: `${cityBasePath}/insights`, eventKey: 'insights', label: 'City insights' },
        { to: `${cityBasePath}/universities`, eventKey: 'universities', label: 'Universities' },
        { to: `${cityBasePath}/comments`, eventKey: 'comments', label: 'Comments' },
    ];

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext({
        pageType: 'discovery-city',
        discoveryType: 'cities',
        cityId: actualCityId,
        cityName: cityData?.name || cityData?.cityName || '',
        countryName: cityData?.country?.name || '',
        activeTab: 'Universities',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'cities',
            selectedCity: cityData?.name || cityData?.cityName || '',
            selectedCountry: cityData?.country?.name || '',
        }),
    });

    const universitiesContent = (
        <>
            {/* Search */}
            <Row className="mb-3">
                <Col xs={12} md={4}>
                    <Form.Control
                        type="text"
                        placeholder="Search universities..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </Col>
            </Row>

            {filteredUniversities.length === 0 ? (
                <Row>
                    <Col md={12}>
                        <Row className="mb-3">
                            <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                <Image src={icNoInsights} alt="No Universities" className="mb-4" />
                                <h2 className="mb-3">No universities yet.</h2>
                                <p className="mb-4">City universities will be displayed here.</p>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            ) : (
                <>
                    <Row>
                        <Col>
                            <div className="table-responsive list">
                                <Table>
                                    <thead>
                                        <tr>
                                            <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>
                                                <div>
                                                    University Name
                                                    <span>
                                                        <FaCaretUp className={sort.by === 'name' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                        <FaCaretDown className={sort.by === 'name' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                    </span>
                                                </div>
                                            </th>
                                            <th onClick={() => toggleSort('address')} style={{ cursor: 'pointer' }}>
                                                <div>
                                                    Address
                                                    <span>
                                                        <FaCaretUp className={sort.by === 'address' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                        <FaCaretDown className={sort.by === 'address' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                    </span>
                                                </div>
                                            </th>
                                            <th onClick={() => toggleSort('telephone')} style={{ cursor: 'pointer' }}>
                                                <div>
                                                    Telephone
                                                    <span>
                                                        <FaCaretUp className={sort.by === 'telephone' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                        <FaCaretDown className={sort.by === 'telephone' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                    </span>
                                                </div>
                                            </th>
                                            <th onClick={() => toggleSort('url')} style={{ cursor: 'pointer' }}>
                                                <div>
                                                    Website
                                                    <span>
                                                        <FaCaretUp className={sort.by === 'url' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                        <FaCaretDown className={sort.by === 'url' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                    </span>
                                                </div>
                                            </th>
                                            <th onClick={() => toggleSort('clasification')} style={{ cursor: 'pointer' }}>
                                                <div>
                                                    Classification
                                                    <span>
                                                        <FaCaretUp className={sort.by === 'clasification' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                        <FaCaretDown className={sort.by === 'clasification' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                    </span>
                                                </div>
                                            </th>
                                            <th onClick={() => toggleSort('studentsNumber')} style={{ cursor: 'pointer' }}>
                                                <div>
                                                    Number of students
                                                    <span>
                                                        <FaCaretUp className={sort.by === 'studentsNumber' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                        <FaCaretDown className={sort.by === 'studentsNumber' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                    </span>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentUniversities.map((u) => {
                                            const href = normalizeUrl(u.url);
                                            return (
                                                <tr key={u._id || u.name}>
                                                    <td>{u.name || 'N/A'}</td>
                                                    <td>{u.address || 'N/A'}</td>
                                                    <td>{u.telephone || 'N/A'}</td>
                                                    <td>
                                                        {href
                                                            ? <a href={href} target="_blank" rel="noopener noreferrer">{u.url}</a>
                                                            : '—'}
                                                    </td>
                                                    <td>{u.classification || 'N/A'}</td>
                                                    <td>{u.studentsNumber || 'N/A'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>

                    <Row className="pagination-wrapper">
                        <Col className="d-flex align-items-center">
                            <span>Total&nbsp;{filteredUniversities.length}&nbsp;universities</span>
                            <Pagination>
                                <Pagination.Prev
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                />
                                {[...Array(totalPages).keys()].map(number => (
                                    <Pagination.Item
                                        key={number + 1}
                                        active={number + 1 === currentPage}
                                        onClick={() => setCurrentPage(number + 1)}
                                    >
                                        {number + 1}
                                    </Pagination.Item>
                                ))}
                                <Pagination.Next
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </Col>
                        <Col className="pagination-controls">
                            <div className="pagination-controls-per-page">
                                <Form.Select
                                    value={rowsPerPage}
                                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                    style={{ width: 'auto', marginRight: '10px' }}
                                >
                                    <option value="10">10 / page</option>
                                    <option value="20">20 / page</option>
                                    <option value="40">40 / page</option>
                                </Form.Select>
                            </div>
                            <label className="pagination-controls-goto-label" htmlFor="goto-page-universities">
                                Go to
                            </label>
                            <Form.Control
                                id="goto-page-universities"
                                type="number"
                                className="pagination-controls-goto-input"
                                value={currentPage}
                                onChange={(e) => {
                                    const newPage = Number(e.target.value);
                                    if (newPage >= 1 && newPage <= totalPages) {
                                        setCurrentPage(newPage);
                                    }
                                }}
                                onBlur={() => {
                                    if (currentPage < 1) setCurrentPage(1);
                                    if (currentPage > totalPages) setCurrentPage(totalPages);
                                }}
                                style={{ textAlign: 'center' }}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </>
    );

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">{error}</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!cityData) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">City not found.</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={cityData?.name} subPage="Universities" fromCountry={countryCode} countryName={cityData?.country?.name} />
                        </Col>
                    </Row>

                    {isMobile ? (
                        <Row className="g-0 country-detail-mobile city-detail-mobile">
                            <Col className="p-0">
                                <CityDetailHero
                                    cityName={cityData.name}
                                    url={safeUrlHref ? { href: safeUrlHref, label: cityData.url } : null}
                                    countryName={cityData.country?.name}
                                />
                                <CityDetailMobileTabs items={discoveryCityNavItems} />
                                <Card className="country-detail-content-card mb-4">
                                    <Card.Body>
                                        <Card className="company-detail-section mb-4 city-universities-section">
                                            <Card.Body>
                                                <h3 className="company-detail-section-title">
                                                    Universities
                                                </h3>
                                                <hr />
                                                <MobileUniversityList
                                                    key={cityBasePath}
                                                    universities={universities}
                                                    cityId={actualCityId}
                                                    countryId={countryCode}
                                                    searchQuery={searchQuery}
                                                    onSearchChange={setSearchQuery}
                                                />
                                            </Card.Body>
                                        </Card>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    ) : (
                    <Row>
                        <Col>
                            <Card className="mb-4">
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <Row>
                                                <Col>
                                                    <Card className="card-premium">
                                                        <Card.Body>
                                                            <Row className="align-items-center">
                                                                <Col>
                                                                    <h3 className="text-start">City</h3>
                                                                    <hr />
                                                                </Col>
                                                            </Row>
                                                            <Row className="mb-4">
                                                                <Col md={8} className="d-flex">
                                                                    <div>
                                                                        <h1>{cityData.name || '—'}</h1>
                                                                        <p>
                                                                            {safeUrlHref ? (
                                                                                <a href={safeUrlHref} target="_blank" rel="noopener noreferrer">
                                                                                    {cityData.url}
                                                                                </a>
                                                                            ) : cityData.url ? cityData.url : '—'}
                                                                            {cityData.country?.name && (
                                                                                <>
                                                                                    <span className="dot"></span>
                                                                                    Country: <strong>{cityData.country.name}</strong>
                                                                                </>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            <Row className="mb-5">
                                                <Col>
                                                    <NavTabs items={discoveryCityNavItems} />
                                                </Col>
                                            </Row>

                                            {/* Universities card */}
                                            <Card className="mb-4">
                                                <Card.Body>
                                                    <Row className="align-items-center">
                                                        <Col>
                                                            <h3 className="mb-0">Universities</h3>
                                                        </Col>
                                                    </Row>
                                                    <hr className="pt-4" />

                                                    {universitiesContent}
                                                </Card.Body>
                                            </Card>

                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    )}
                </Container>
            </main>
        </>
    );
};

export default DiscoveryCityViewUniversities;
